import { Helmet } from 'react-helmet-async';
import PageLayout from '../../components/PageLayout';
import { MapPin, CheckCircle2, Building2 } from 'lucide-react';

const ModernHomeIritty = () => {
  return (
    <>
      <Helmet>
        <title>Modern Home Construction Project in Iritty | Master Home</title>
        <meta name="description" content="Read our case study on building a stunning modern home in Iritty. Master Home handled tight budgets and laterite soil conditions with expert precision." />
        <link rel="canonical" href="https://masterhome.co.in/projects/modern-home-iritty" />
      </Helmet>

      <PageLayout
        title="Modern Home Construction in Iritty"
        subtitle="A contemporary architectural masterpiece built securely on Kerala's challenging terrain."
      >
        <div className="space-y-16">
          {/* Hero Gallery */}
          <div className="rounded-2xl overflow-hidden shadow-2xl relative">
            <img
              src="/house-renovation-iritty.jpg"
              alt="Modern house exterior construction in Iritty"
              className="w-full h-auto aspect-[21/9] object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-8">
              <h2 className="text-3xl font-bold text-white">Project Value: ₹85 Lakhs</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="md:col-span-2 space-y-12">
              {/* Overview */}
              <section className="prose max-w-none text-gray-700">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b pb-4">Project Overview</h2>
                <p className="text-lg">
                  This 3-bedroom modern residence in Iritty highlights our ability to deliver striking contemporary elevations while maximizing interior space efficiency. Commissioned by a young professional family, the goal was to create a minimalist, low-maintenance home filled with natural light.
                </p>
                <div className="flex flex-wrap gap-4 mt-6">
                  <span className="bg-primary-50 text-primary-700 px-4 py-2 rounded-full font-semibold flex items-center gap-2"><MapPin size={18} /> Near Main Town, Iritty</span>
                  <span className="bg-primary-50 text-primary-700 px-4 py-2 rounded-full font-semibold flex items-center gap-2"><Building2 size={18} /> Nuclear Family</span>
                </div>
              </section>

              {/* Challenge */}
              <section className="bg-orange-50 p-8 rounded-2xl border border-orange-100">
                <h3 className="text-2xl font-bold text-orange-900 mb-4">The Challenge</h3>
                <ul className="space-y-4 text-orange-800">
                  <li className="flex gap-3"><span className="font-bold shrink-0">1. Budget Optimization:</span> The client had a strict ceiling of ₹85 Lakhs for turnkey handover, leaving no room for material wastage or redesigns.</li>
                  <li className="flex gap-3"><span className="font-bold shrink-0">2. Hard Laterite Soil:</span> Iritty's notoriously hard laterite bedrock made trenching and foundation plinth work incredibly difficult.</li>
                  <li className="flex gap-3"><span className="font-bold shrink-0">3. Contemporary Flat Roofs:</span> The client wanted a flat roof design, which is highly prone to leaks during the torrential Kerala monsoons if not engineered perfectly.</li>
                </ul>
              </section>

              {/* Solution */}
              <section className="bg-blue-50 p-8 rounded-2xl border border-blue-100">
                <h3 className="text-2xl font-bold text-blue-900 mb-4">The Solution & Execution</h3>
                <p className="text-blue-800 mb-6">
                  Our structural engineers employed mechanized pneumatic drilling to bypass the laterite rock efficiently, saving labor days. To guarantee zero leaks on the flat roof, we executed a triple-layer Dr. Fixit waterproofing protocol integrated directly into the concrete slab.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-200">
                    <h4 className="font-bold flex items-center gap-2 mb-2"><CheckCircle2 className="text-blue-600" /> Cost Control</h4>
                    <p className="text-sm text-gray-600">Locked in wholesale prices for steel and cement locally in Iritty prior to market inflation spikes.</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-200">
                    <h4 className="font-bold flex items-center gap-2 mb-2"><CheckCircle2 className="text-blue-600" /> Modern Aesthetics</h4>
                    <p className="text-sm text-gray-600">Used exposed brick cladding accents and large UPVC windows to achieve the contemporary look gracefully.</p>
                  </div>
                </div>
              </section>

            </div>

            {/* Sidebar Details */}
            <div className="space-y-8">
              <div className="glass-card p-6 bg-gray-50 rounded-2xl border border-gray-100">
                <h3 className="text-xl font-bold mb-6">Project Details</h3>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Square Footage</span>
                    <span className="font-bold text-gray-900">2,100 sq.ft</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Duration</span>
                    <span className="font-bold text-gray-900">8 Months</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Bedrooms</span>
                    <span className="font-bold text-gray-900">3 BHK</span>
                  </div>
                  <div className="flex justify-between pb-2">
                    <span className="text-gray-500">Cost Range</span>
                    <span className="font-bold text-gray-900">₹80 - 90 Lakhs</span>
                  </div>
                </div>
              </div>

              {/* Client Testimonial */}
              <div className="bg-gray-900 text-white p-8 rounded-2xl shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 text-9xl text-white/10 font-serif leading-none transform translate-x-4 -translate-y-4">"</div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-yellow-400 font-bold text-xl">★★★★★</span>
                </div>
                <p className="relative z-10 text-lg leading-relaxed mb-6 font-medium italic">
                  "I was terrified of budget overruns, but Master Home kept their promise. Our modern home in Iritty looks exactly like the 3D elevation they showed us. Very professional civil contractors!"
                </p>
                <div className="flex items-center gap-4 border-t border-white/20 pt-4">
                  <div className="bg-primary-600 w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">S</div>
                  <div>
                    <h4 className="font-bold">Suresh Menon</h4>
                    <p className="text-gray-300 text-sm">Tech Professional, Iritty</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12 bg-primary-50 p-8 rounded-2xl border border-primary-100">
            <h2 className="text-2xl font-bold mb-4 text-primary-900">Planning a Build in Iritty?</h2>
            <a href="tel:+919567810944" className="inline-flex items-center justify-center px-8 py-4 text-white bg-primary-600 rounded-full font-bold hover:bg-primary-700 transition-colors shadow-lg">
              Consult With Our Engineers
            </a>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default ModernHomeIritty;
